import { Component, Input, Output, EventEmitter, forwardRef, ElementRef, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { Court } from '@ee/common/models';

/**
 * A dropdown-style courthouse selector component that displays court cards in a panel.
 * Supports multiple selection and integrates with Angular reactive forms.
 */
@Component({
  selector: 'ee-courthouse-selector',
  templateUrl: './courthouse-selector.component.html',
  styleUrls: ['./courthouse-selector.component.scss'],
  standalone: true,
  imports: [CommonModule, CdkConnectedOverlay, CdkOverlayOrigin],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CourthouseSelectorComponent),
      multi: true
    }
  ]
})
export class CourthouseSelectorComponent implements ControlValueAccessor, OnChanges {
  @Input() courts: Court[] = [];
  @Input() disabled = false;
  @Input() label = 'Court(s)';
  @Input() hint = 'Optional.';
  @Output() selectionChange = new EventEmitter<Court[]>();

  selectedCourts: Court[] = [];
  isOpen = false;

  private onChange: (value: Court[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['courts'] && this.courts.length > 0 && this.selectedCourts.length > 0) {
      // Re-map selected courts to the new courts array to ensure proper object matching
      const remappedCourts = this.selectedCourts.map(sc => {
        const scId = (sc as any).id || (sc as any)._id;
        const match = this.courts.find(c => {
          const cId = (c as any).id || (c as any)._id;
          if (scId && cId) return scId === cId;
          return c.name === sc.name;
        });
        return match ?? sc;
      });
      
      const hasChanges = remappedCourts.some((rc, i) => rc !== this.selectedCourts[i]);
      if (hasChanges) {
        this.selectedCourts = remappedCourts;
        this.cdr.detectChanges();
      }
    }
  }

  togglePanel(event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.onTouched();
    }
  }

  closePanel(): void {
    this.isOpen = false;
  }

  get triggerText(): string {
    const count = this.selectedCourts?.length ?? 0;
    if (count === 0) return 'Select court(s)';
    if (count === 1) return this.selectedCourts[0].name;
    return `${count} Courts Selected`;
  }

  isSelected(court: Court): boolean {
    if (!this.selectedCourts || this.selectedCourts.length === 0) {
      return false;
    }
    return this.selectedCourts.some(c => {
      const selectedId = (c as any).id || (c as any)._id;
      const courtId = (court as any).id || (court as any)._id;
      if (selectedId && courtId) {
        return selectedId === courtId;
      }
      return !!(c.name && court.name && c.name === court.name);
    });
  }

  toggleSelection(court: Court, event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;

    if (this.isSelected(court)) {
      const courtId = (court as any).id || (court as any)._id;
      this.selectedCourts = this.selectedCourts.filter(c => {
        const selectedId = (c as any).id || (c as any)._id;
        if (selectedId && courtId) {
          return selectedId !== courtId;
        }
        return c.name !== court.name;
      });
    } else {
      this.selectedCourts = [...this.selectedCourts, court];
    }

    this.onChange(this.selectedCourts);
    this.selectionChange.emit(this.selectedCourts);
  }

  formatStreetAddress(court: Court): string {
    return court.street_address || '';
  }

  formatCityStateZip(court: Court): string {
    const parts = [court.city, court.state, court.zip_code].filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]}, ${parts.slice(1).join(' ')}`;
    }
    return parts.join(' ');
  }

  writeValue(value: Court[]): void {
    this.selectedCourts = value || [];
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: Court[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
